import { ArrowLeft, Calendar, Clock, User, CheckCircle, Gift, Heart, Share2, MessageCircle, Star } from 'lucide-react';
import { useTranslation } from '../utils/useTranslation';

interface BlogPost1Props {
    onBack: () => void;
}

export function BlogPost1({ onBack }: BlogPost1Props) {
    const { t, tObject } = useTranslation();

    // Get all translations for this blog post
    const meta = tObject('blogPost1.meta') as any;
    const intro = tObject('blogPost1.intro') as any;
    const tip1 = tObject('blogPost1.tip1') as any;
    const tip2 = tObject('blogPost1.tip2') as any;
    const tip3 = tObject('blogPost1.tip3') as any;
    const tip4 = tObject('blogPost1.tip4') as any;
    const tip5 = tObject('blogPost1.tip5') as any;
    const tip6 = tObject('blogPost1.tip6') as any;
    const tip7 = tObject('blogPost1.tip7') as any;
    const tip8 = tObject('blogPost1.tip8') as any;
    const tip9 = tObject('blogPost1.tip9') as any;
    const tip10 = tObject('blogPost1.tip10') as any;
    const cta = tObject('blogPost1.cta') as any;

    return (
        <div className="min-h-screen bg-white pt-24 pb-12">
            {/* Hero Section */}
            <div className="relative h-[500px] w-full overflow-hidden mb-12">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-green-700 opacity-90"></div>
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1513885535751-8b9238bd345a?auto=format&fit=crop&q=80')] bg-cover bg-center mix-blend-overlay"></div>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center">
                    <span className="px-4 py-1 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium mb-6 border border-white/30">
                        {meta?.badge}
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold max-w-4xl mb-6 leading-tight drop-shadow-lg">
                        {meta?.title}
                    </h1>
                    <div className="flex flex-wrap items-center justify-center gap-6 text-sm md:text-base opacity-90">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            <span>{meta?.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            <span>{meta?.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            <span>{meta?.readTime}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 max-w-4xl">
                <button
                    onClick={onBack}
                    className="group flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors mb-8 font-medium"
                >
                    <div className="p-2 bg-gray-100 rounded-full group-hover:bg-red-50 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </div>
                    {meta?.backButton}
                </button>

                <article className="prose prose-lg max-w-none">
                    <p className="lead text-2xl text-gray-600 mb-10 font-light leading-relaxed border-l-4 border-red-500 pl-6 italic">
                        {intro?.lead}
                    </p>

                    <div className="bg-red-50 rounded-2xl p-8 mb-12 border border-red-100">
                        <h3 className="text-red-800 font-bold text-xl mb-4 flex items-center gap-2">
                            <Star className="w-5 h-5 fill-current" />
                            {intro?.learningTitle}
                        </h3>
                        <ul className="grid md:grid-cols-2 gap-3 list-none p-0 m-0">
                            {intro?.learningItems?.map((item: string, i: number) => (
                                <li key={i} className="flex items-center gap-2 text-gray-700">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Tip 1 */}
                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-red-600 text-white rounded-lg text-xl font-bold">1</span>
                        {tip1?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{tip1?.p1}</p>
                    <p className="text-gray-700 mb-6">{tip1?.p2}</p>
                    <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                        <li><strong>{t('blogPost1.tip1.reasons.confirmation').split(':')[0]}:</strong> {t('blogPost1.tip1.reasons.confirmation').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost1.tip1.reasons.economy').split(':')[0]}:</strong> {t('blogPost1.tip1.reasons.economy').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost1.tip1.reasons.chaos').split(':')[0]}:</strong> {t('blogPost1.tip1.reasons.chaos').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost1.tip1.reasons.delivery').split(':')[0]}:</strong> {t('blogPost1.tip1.reasons.delivery').split(':').slice(1).join(':')}</li>
                    </ul>

                    {/* Tip 2 */}
                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-green-600 text-white rounded-lg text-xl font-bold">2</span>
                        {tip2?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{tip2?.p1}</p>
                    <p className="text-gray-700 mb-6">{tip2?.p2}</p>
                    <div className="bg-white border-l-4 border-green-500 shadow-sm p-6 mb-8">
                        <h4 className="font-bold text-green-800 mb-2">{tip2?.proTip?.title}</h4>
                        <p className="text-gray-600 m-0">{tip2?.proTip?.content}</p>
                    </div>

                    {/* Tip 3 */}
                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-purple-600 text-white rounded-lg text-xl font-bold">3</span>
                        {tip3?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{tip3?.p1}</p>
                    <p className="text-gray-700 mb-6">{tip3?.p2}</p>
                    <div className="grid md:grid-cols-3 gap-4 mb-8">
                        <div className="bg-gray-50 p-6 rounded-xl text-center shadow-sm hover:shadow-md transition-all">
                            <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Share2 className="w-7 h-7" />
                            </div>
                            <h4 className="font-bold mb-2 text-lg">{tip3?.features?.draw?.title}</h4>
                            <p className="text-sm text-gray-600 leading-relaxed">{tip3?.features?.draw?.description}</p>
                        </div>
                        <div className="bg-gray-50 p-6 rounded-xl text-center shadow-sm hover:shadow-md transition-all">
                            <div className="w-14 h-14 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <MessageCircle className="w-7 h-7" />
                            </div>
                            <h4 className="font-bold mb-2 text-lg">{tip3?.features?.chat?.title}</h4>
                            <p className="text-sm text-gray-600 leading-relaxed">{tip3?.features?.chat?.description}</p>
                        </div>
                        <div className="bg-gray-50 p-6 rounded-xl text-center shadow-sm hover:shadow-md transition-all">
                            <div className="w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Gift className="w-7 h-7" />
                            </div>
                            <h4 className="font-bold mb-2 text-lg">{tip3?.features?.wishlist?.title}</h4>
                            <p className="text-sm text-gray-600 leading-relaxed">{tip3?.features?.wishlist?.description}</p>
                        </div>
                    </div>

                    {/* Tip 4 */}
                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-orange-500 text-white rounded-lg text-xl font-bold">4</span>
                        {tip4?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{tip4?.p1}</p>
                    <p className="text-gray-700 mb-6">{tip4?.p2}</p>
                    <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                        <li><strong>{t('blogPost1.tip4.requirements.variety').split(':')[0]}:</strong> {t('blogPost1.tip4.requirements.variety').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost1.tip4.requirements.links').split(':')[0]}:</strong> {t('blogPost1.tip4.requirements.links').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost1.tip4.requirements.specs').split(':')[0]}:</strong> {t('blogPost1.tip4.requirements.specs').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost1.tip4.requirements.negative').split(':')[0]}:</strong> {t('blogPost1.tip4.requirements.negative').split(':').slice(1).join(':')}</li>
                    </ul>

                    {/* Tip 5 */}
                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-pink-600 text-white rounded-lg text-xl font-bold">5</span>
                        {tip5?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{tip5?.intro}</p>
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                        <div className="border rounded-xl p-6 hover:shadow-lg transition-all bg-white">
                            <h4 className="font-bold text-lg mb-3 text-pink-600 flex items-center gap-2">{tip5?.themes?.cult?.title}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed">{tip5?.themes?.cult?.description}</p>
                        </div>
                        <div className="border rounded-xl p-6 hover:shadow-lg transition-all bg-white">
                            <h4 className="font-bold text-lg mb-3 text-green-600 flex items-center gap-2">{tip5?.themes?.sustainable?.title}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed">{tip5?.themes?.sustainable?.description}</p>
                        </div>
                        <div className="border rounded-xl p-6 hover:shadow-lg transition-all bg-white">
                            <h4 className="font-bold text-lg mb-3 text-blue-600 flex items-center gap-2">{tip5?.themes?.chocolate?.title}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed">{tip5?.themes?.chocolate?.description}</p>
                        </div>
                        <div className="border rounded-xl p-6 hover:shadow-lg transition-all bg-white">
                            <h4 className="font-bold text-lg mb-3 text-yellow-600 flex items-center gap-2">{tip5?.themes?.funny?.title}</h4>
                            <p className="text-gray-600 text-sm leading-relaxed">{tip5?.themes?.funny?.description}</p>
                        </div>
                    </div>

                    {/* Tip 6 */}
                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-indigo-600 text-white rounded-lg text-xl font-bold">6</span>
                        {tip6?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{tip6?.p1}</p>
                    <p className="text-gray-700 mb-6">{tip6?.p2}</p>

                    {/* Tip 7 */}
                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-teal-600 text-white rounded-lg text-xl font-bold">7</span>
                        {tip7?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{tip7?.p1}</p>
                    <p className="text-gray-700 mb-6">{tip7?.p2}</p>

                    {/* Tip 8 */}
                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-cyan-600 text-white rounded-lg text-xl font-bold">8</span>
                        {tip8?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{tip8?.p1}</p>
                    <p className="text-gray-700 mb-6">{tip8?.p2}</p>
                    <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
                        <li><strong>{t('blogPost1.tip8.details.music').split(':')[0]}:</strong> {t('blogPost1.tip8.details.music').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost1.tip8.details.food').split(':')[0]}:</strong> {t('blogPost1.tip8.details.food').split(':').slice(1).join(':')}</li>
                        <li><strong>{t('blogPost1.tip8.details.decoration').split(':')[0]}:</strong> {t('blogPost1.tip8.details.decoration').split(':').slice(1).join(':')}</li>
                    </ul>

                    {/* Tip 9 */}
                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-rose-600 text-white rounded-lg text-xl font-bold">9</span>
                        {tip9?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{tip9?.intro}</p>
                    <ul className="space-y-4 mb-8">
                        <li className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-100">
                            <strong className="text-rose-600 text-lg block mb-2">{tip9?.dynamics?.whoAmI?.title}</strong>
                            <span className="text-gray-700">{tip9?.dynamics?.whoAmI?.description}</span>
                        </li>
                        <li className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-100">
                            <strong className="text-rose-600 text-lg block mb-2">{tip9?.dynamics?.thief?.title}</strong>
                            <span className="text-gray-700">{tip9?.dynamics?.thief?.description}</span>
                        </li>
                    </ul>

                    {/* Tip 10 */}
                    <h2 className="text-3xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3">
                        <span className="flex items-center justify-center w-10 h-10 bg-gray-800 text-white rounded-lg text-xl font-bold">10</span>
                        {tip10?.title}
                    </h2>
                    <p className="text-gray-700 mb-6">{tip10?.p1}</p>
                    <p className="text-gray-700 mb-6">{tip10?.p2}</p>

                    <hr className="my-12 border-gray-200" />

                    {/* CTA */}
                    <div className="bg-gradient-to-r from-red-600 to-green-600 rounded-2xl p-8 md:p-12 text-white text-center shadow-2xl relative overflow-hidden">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/snow.png')] opacity-20"></div>
                        <Heart className="w-16 h-16 mx-auto mb-6 text-white animate-pulse relative z-10" />
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 relative z-10">{cta?.title}</h2>
                        <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto relative z-10 leading-relaxed">
                            {cta?.description}
                        </p>
                        <button
                            onClick={onBack}
                            className="relative z-10 bg-white text-red-600 px-10 py-5 rounded-full font-bold text-lg hover:bg-gray-100 hover:scale-105 transition-all shadow-xl uppercase tracking-wide"
                        >
                            {cta?.button}
                        </button>
                    </div>
                </article>
            </div>
        </div>
    );
}